/* eslint-disable sonarjs/no-duplicate-string */
import type { Agent } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import type { OperatingSystem } from '@studio/react-utils/user-agent';
import deepFreeze from 'deep-freeze';
import type { LinkRequest } from '#~/api/agent/types.js';

/** Route paths. */
export const Paths = deepFreeze({
  agent: {
    $: 'agent/:agentId',
    resolve: (id: Agent['id']): string => `/${Paths.agent.$.replace(':agentId', id)}`,

    withWait: {
      $: 'agent/:agentId/wait',
      resolve: (id: Agent['id']): string => `/${Paths.agent.withWait.$.replace(':agentId', id)}`
    },

    link: {
      auth: {
        $: 'agent/:agentId/link/:requestId/studio/:roomId/auth',
        resolve: (agentId: Agent['id'], requestId: LinkRequest['id'], roomId: Room['id']): string =>
          `/${Paths.agent.link.auth.$.replace(':agentId', agentId).replace(':requestId', requestId).replace(':roomId', roomId)}`
      },
      complete: {
        $: 'agent/:agentId/link/:roomId/complete',
        resolve: (roomId: Room['id'], agentId: Agent['id']): string =>
          `/${Paths.agent.link.complete.$.replace(':roomId', roomId).replace(':agentId', agentId)}`
      },
      configure: {
        $: 'agent/:agentId/link/:requestId/configure',
        resolve: (agentId: Agent['id'], requestId: LinkRequest['id']): string =>
          `/${Paths.agent.link.configure.$.replace(':agentId', agentId).replace(
            ':requestId',
            requestId
          )}`
      },
      confirm: {
        $: 'agent/:agentId/link/:requestId',
        resolve: (agentId: Agent['id'], requestId: LinkRequest['id']): string =>
          `/${Paths.agent.link.confirm.$.replace(':agentId', agentId).replace(
            ':requestId',
            requestId
          )}`
      },
      createStudio: {
        $: 'agent/:agentId/link/:requestId/studio/create',
        resolve: (agentId: Agent['id'], requestId: LinkRequest['id']): string =>
          `/${Paths.agent.link.createStudio.$.replace(':agentId', agentId).replace(
            ':requestId',
            requestId
          )}`
      },
      selectStudio: {
        $: 'agent/:agentId/link/:requestId/studio/select',
        resolve: (agentId: Agent['id'], requestId: LinkRequest['id']): string =>
          `/${Paths.agent.link.selectStudio.$.replace(':agentId', agentId).replace(
            ':requestId',
            requestId
          )}`
      }
    },
    setup: {
      $: 'agent/setup',
      download: {
        $: 'agent/setup/download/:os/:key',
        resolve: (os: OperatingSystem, key: string) =>
          `/${Paths.agent.setup.download.$}`.replace(':os', os).replace(':key', key)
      },
      link: 'agent/setup/link',
      // ENG-4175: post-auth first-login segmentation wizard, rendered immediately
      // before the download/add-camera step (use-root-bootstrap intercepts here).
      prequal: 'agent/setup/prequal',
      select: 'agent/setup/select'
    },
    unlink: 'agent/unlink',
    update: 'agent/update',
    restart: 'agent/restart',
    linking: 'agent/linking',
    restartWithAgent: {
      $: `agent/restart/:agentId`,
      resolve: (id: Agent['id']): string =>
        `/${Paths.agent.restartWithAgent.$.replace(':agentId', id)}`
    },
    shutdown: 'agent/shutdown'
  },
  login: 'login',
  // ENG-3451: account-collision page — public (the user is by definition NOT
  // authenticated when they land here), driven only by the error_description
  // query param matching the verified COLLISION: prefix (auth/collision.ts).
  loginCollision: 'login/collision',
  logout: 'logout',
  // ENG-4037 workspace-entry picker: post-login landing for multi-studio
  // accounts with no remembered/auto-link room (see use-root-bootstrap.ts).
  selectStudio: 'select-studio',
  oauth2: {
    callback: 'oauth/callback'
  },
  register: 'register',
  room: {
    $: 'room/:roomId',
    resolve: (id: Room['id']): string => `/${Paths.room.$.replace(':roomId', id)}`,
    inspect: {
      $: 'room/:roomId/inspect',
      resolve: (id: Room['id']): string => `/${Paths.room.inspect.$.replace(':roomId', id)}`
    },
    boot: {
      $: 'room/:roomId/boot',
      resolve: (id: Room['id']): string => `/${Paths.room.boot.$.replace(':roomId', id)}`
    },
    billingReturn: {
      $: 'room/:roomId/billing/:mode/:status?',
      resolve: (id: Room['id'], mode: string): string =>
        `/${Paths.room.billingReturn.$.replace(':roomId', id).replace(':mode', mode).replace(':status', '')}`
    }
  },
  invite: {
    preRedeem: {
      $: 'invite/:inviteId/:redemptionToken',
      resolve: (inviteId: string, redemptionToken: string): string =>
        `/${Paths.invite.preRedeem.$.replace(':inviteId', inviteId).replace(
          ':redemptionToken',
          redemptionToken
        )}`
    },
    postRedeem: {
      $: 'invite/:inviteId/:redemptionToken/complete',
      resolve: (inviteId: string, redemptionToken: string): string =>
        `/${Paths.invite.postRedeem.$.replace(':inviteId', inviteId).replace(
          ':redemptionToken',
          redemptionToken
        )}`
    }
  },
  exhibits: {
    $: 'exhibits/*'
  },
  buildInfo: 'build-info'
});
